import React from 'react';
import { connect } from 'react-redux';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/detail/DateChipContainer/DateChipContainer.selectors';
import DateChip from 'component/common/DateChip/DateChip';
import { isToday, isTomorrow, parseISO } from 'date-fns';
import { Box, Popover, TextLink } from '@drone-js/core';
import { AvailabilitySchedule } from 'gql/autogen/AvailabilitySchedule';
import { getDateInYmdFormat, isSameDate } from 'core-framework/util/datetime/date';
import { SearchQueryKey } from 'core-framework/constant/SearchQueryKey';
import { replaceState } from 'core-framework/window/history';
import { QueryString } from 'core/utils/queryString/queryString';
import { useLocation } from 'react-router-dom';
import PopoverCalendar from 'component/page/detail/PopoverCalendar/PopoverCalendar';
import { addEventListenerToWindow, removeEventListenerFromWindow } from 'core/utils/domUtils';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesCloseDatePickerTextlinkClickEventActivitiesFieldsV1,
    ActivitiesMoreDatesButtonClickEventActivitiesFieldsV2,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesCloseDatePickerTextlinkClickEventActivitiesManifestV1,
    ActivitiesMoreDatesButtonClickEventActivitiesManifestV2,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { dateChipGenerator } from 'component/page/detail/detailUtils';
import { roundNumberWithDecimal } from 'core/utils/roundNumberWithDecimal';

interface CalendarContainerProps {
    cms: CmsValue;
    isBiggerBreakpoint: boolean;
    showCalendar: boolean;
    closeCalendarPopover: () => void;
    toggleCalendarPopover: () => void;
}

const CALENDAR_DESKTOP_MAX_WIDTH = '320px';

const CalendarContainer: React.FC<CalendarContainerProps> = (props) => {
    const { cms, isBiggerBreakpoint, showCalendar, closeCalendarPopover, toggleCalendarPopover } = props;
    const moreDateButtonRef = React.useRef<HTMLButtonElement>(null);

    const onCloseDatePickerTextLinkClick = () => {
        trackElementEvent<ActivitiesCloseDatePickerTextlinkClickEventActivitiesFieldsV1>(
            ActivitiesCloseDatePickerTextlinkClickEventActivitiesManifestV1.ids,
            {},
            { action_element_value: 'activity_level' },
        );
        closeCalendarPopover();
    };

    if (isBiggerBreakpoint) {
        return (
            // eslint-disable-next-line @agoda/agoda-a11y/no-incorrect-drone-box-usage -- @agoda/agoda-a11y/no-incorrect-drone-box-usage[a7249a26-f747-4dca-8d22-87c7c9c416bb]
            <Popover
                p="s"
                content={
                    <Box maxWidth={CALENDAR_DESKTOP_MAX_WIDTH} pb="8">
                        <PopoverCalendar onSelectDateCallback={closeCalendarPopover} isPopover={true} />
                    </Box>
                }
                zIndex={1020}
                visible={showCalendar}
                onBlur={closeCalendarPopover}
                autoHeightResize={false}
                // Need flexBasis because inside Popover have nested layers of flex Box which make shrink scaling different from other chips
                flexWrapper={{ flexShrink: 1, flexGrow: 1, flexBasis: 8 }}
            >
                <DateChip type="MoreDates" cms={cms} handleClick={toggleCalendarPopover} isAvailable={true} isPopoverCalendarOpen={showCalendar} buttonRef={moreDateButtonRef} />
            </Popover>
        );
    }

    if (showCalendar) {
        return (
            <Box width="100%" textAlign="right" px="12">
                <TextLink
                    data-element-name="activities-close-date-picker-textlink"
                    data-testid="activities-close-date-picker-textlink"
                    hierarchy="primary"
                    label={cms[290834]}
                    textVariant="body-s"
                    onClick={onCloseDatePickerTextLinkClick}
                    role="dialog"
                    aria-modal={true}
                    aria-label={cms[316460]}
                />
                <Box mt="16">
                    <PopoverCalendar onSelectDateCallback={closeCalendarPopover} />
                </Box>
            </Box>
        );
    }

    return <DateChip type="MoreDates" cms={cms} handleClick={toggleCalendarPopover} isAvailable={true} isPopoverCalendarOpen={showCalendar} />;
};
CalendarContainer.displayName = 'CalendarContainer';

const getBadge = (date: Date, cms: CmsValue) => {
    if (isToday(date)) return cms[72774];
    if (isTomorrow(date)) return cms[72780];
    return '';
};

const DateChipContainer: React.FC<Props> = (props) => {
    const { cms, selectedDate, activityLevelOfferPrice, activityLevelCalendarPrice, isDetailPageForSimCardEnabled } = props;
    const locationSearch = useLocation().search;
    const isTabletOrDesktop = props.screenSize.tabletOrDesktop;
    const dateChipsSize = isTabletOrDesktop ? 7 : 3;
    const selectedDateString = getDateInYmdFormat(selectedDate);
    const [dateChips, setDateChips] = React.useState<AvailabilitySchedule[]>([]);
    const [showCalendar, setShowCalendar] = React.useState<boolean>(false);
    const [isPopoverCalendarOpen, setIsPopoverCalendarOpen] = React.useState(false);

    // Reset state when window is resized avoid issue with breakpoint change
    React.useEffect(() => {
        const handleResize = () => setShowCalendar(false);

        addEventListenerToWindow('resize', handleResize);
        return () => {
            removeEventListenerFromWindow('resize', handleResize);
        };
    }, [isTabletOrDesktop]);

    React.useEffect(() => {
        if (!props.isScheduleReady) {
            return;
        }

        const dateChipsSchedule = dateChipGenerator(dateChipsSize, selectedDate, dateChips, props.availabilitySchedule, props.isSchedulesAllUnavailable);
        setDateChips(dateChipsSchedule);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateChips, dateChipsSize, props.isScheduleReady, selectedDate]);

    if (props.availabilitySchedule.length === 0) return null;

    const onDateChipClick = (date?: Date) => {
        if (date && !isSameDate(date, selectedDate)) {
            const query = new QueryString(locationSearch);
            query.set(SearchQueryKey.TravelDate, getDateInYmdFormat(date));
            replaceState(`?${query.toString()}`);
            props.resetSelectedOfferCard();
            props.setSelectedDate(props.activityTokenFromState, date);
        }
    };

    const closePopoverCalendar = () => setShowCalendar(false);
    const togglePopoverCalendar = () => {
        trackElementEvent<ActivitiesMoreDatesButtonClickEventActivitiesFieldsV2>(ActivitiesMoreDatesButtonClickEventActivitiesManifestV2.ids, {
            travel_date: selectedDateString,
            final_price: activityLevelOfferPrice,
            // Need to round due to IEEE 754 floating point arithmetic
            price_difference: roundNumberWithDecimal(activityLevelOfferPrice - activityLevelCalendarPrice, 2),
        });
        setShowCalendar(!showCalendar);
        setIsPopoverCalendarOpen(true);
    };

    return (
        <Box
            display="flex"
            mt="16"
            data-testid="date-chip-container"
            width="100%"
            justifyContent="space-between"
            flexWrap={isTabletOrDesktop ? 'nowrap' : 'wrap'}
            mx={isDetailPageForSimCardEnabled ? '0' : '4'}
        >
            {dateChips.map((schedule, index) => {
                if (!schedule.date || !selectedDate || (!isTabletOrDesktop && showCalendar)) return null;
                const selected = selectedDateString === schedule.date;
                const scheduleDate = parseISO(schedule.date);

                return (
                    <DateChip
                        type="DateChip"
                        key={index}
                        cms={cms}
                        date={scheduleDate}
                        selected={selected}
                        isAvailable={schedule.isAvailable}
                        badge={getBadge(scheduleDate, cms)}
                        handleClick={onDateChipClick}
                        isPopoverCalendarOpen={isPopoverCalendarOpen}
                    />
                );
            })}
            <CalendarContainer
                isBiggerBreakpoint={isTabletOrDesktop}
                showCalendar={showCalendar}
                cms={cms}
                closeCalendarPopover={closePopoverCalendar}
                toggleCalendarPopover={togglePopoverCalendar}
            />
        </Box>
    );
};

DateChipContainer.displayName = 'DateChipContainer';
export default connect(mapStateToProps, mapDispatchToProps)(DateChipContainer);
